/**
 * GCIP / Firebase Auth, in a Vite SPA.
 *
 * Ported from Admin Dashboard/src/auth/useFirebaseAuth.ts:27-51 — the same dynamic import,
 * the same onAuthStateChanged-driven status, the same getFreshToken ref. Two differences:
 *
 *  1. Persistence is set from "Keep me signed in" BEFORE any sign-in call. setPersistence
 *     applied afterwards does not retroactively move the session, and the resulting
 *     behaviour ("I unchecked it and it still remembered me") silently breaks a stated
 *     privacy promise.
 *  2. The portal ships NO decodeIdentity equivalent. Role comes from /me, never a claim —
 *     see SessionProvider.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../config.js";
import type { AuthStatus, AuthValue } from "./AuthContext.js";
import { readKeepSignedIn } from "./persistence.js";

// Narrow structural types. The SDK is imported dynamically, so nothing here pulls it into
// the test bundle or onto the critical path of a static auth form.
type FirebaseUser = {
  emailVerified: boolean;
  email: string | null;
  getIdToken: (force?: boolean) => Promise<string>;
  reload: () => Promise<void>;
};

/** The verification link lands on /verify, where the poll picks the flip up. */
function verifyActionCode() {
  return { url: `${config.portalOrigin}/verify` };
}

export function useFirebaseAuth(): AuthValue {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<FirebaseUser | null>(null);
  // Tracked separately from `user` because `user` is always the SAME object reference
  // (see reloadUser) — React cannot see a field flip on it, so the flip lives here.
  const [verifiedState, setVerifiedState] = useState(false);
  const authRef = useRef<any>(null);
  const modRef = useRef<any>(null);

  const load = useCallback(async () => {
    if (authRef.current) return { auth: authRef.current, mod: modRef.current };
    const [{ initializeApp, getApps }, mod] = await Promise.all([
      import("firebase/app"),
      import("firebase/auth"),
    ]);
    const app = getApps()[0] ?? initializeApp(config.firebase);
    const auth = mod.getAuth(app);
    authRef.current = auth;
    modRef.current = mod;
    return { auth, mod };
  }, []);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      const { auth, mod } = await load();
      if (cancelled) return;
      unsub = mod.onAuthStateChanged(auth, (u: FirebaseUser | null) => {
        setUser(u);
        setVerifiedState(Boolean(u?.emailVerified));
        setStatus(u ? "authenticated" : "unauthenticated");
      });
    })();
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [load]);

  /** Persistence must be chosen before the sign-in call, not after it. */
  const applyPersistence = useCallback(
    async (keepSignedIn: boolean) => {
      const { auth, mod } = await load();
      await mod.setPersistence(
        auth,
        keepSignedIn ? mod.browserLocalPersistence : mod.browserSessionPersistence,
      );
    },
    [load],
  );

  const getToken = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      if (!user) return null;
      return user.getIdToken(forceRefresh);
    },
    [user],
  );

  const signUpWithPassword = useCallback(
    async (email: string, password: string) => {
      const { auth, mod } = await load();
      // Signup always uses the default (local) persistence: there is no checkbox on the
      // signup form, and the account is brand new.
      await applyPersistence(true);
      const cred = await mod.createUserWithEmailAndPassword(auth, email, password);
      // The portal sends this itself via the client SDK. Identity's resend endpoint returns
      // 202 and sends nothing (it discards the Admin SDK link), so this is the ONLY sender.
      await mod.sendEmailVerification(cred.user, verifyActionCode());
    },
    [load, applyPersistence],
  );

  const signInWithPassword = useCallback(
    async (email: string, password: string, keepSignedIn: boolean) => {
      const { auth, mod } = await load();
      await applyPersistence(keepSignedIn);
      const cred = await mod.signInWithEmailAndPassword(auth, email, password);
      // Returned from the credential, not read back from state: the hook's `emailVerified`
      // is still the pre-login value inside the caller's closure.
      return Boolean(cred.user.emailVerified);
    },
    [load, applyPersistence],
  );

  const signInWithGoogle = useCallback(
    async (keepSignedIn: boolean) => {
      const { auth, mod } = await load();
      await applyPersistence(keepSignedIn);
      const provider = new mod.GoogleAuthProvider();
      await mod.signInWithPopup(auth, provider);
    },
    [load, applyPersistence],
  );

  const sendVerificationEmail = useCallback(async () => {
    const { auth, mod } = await load();
    if (auth.currentUser) await mod.sendEmailVerification(auth.currentUser, verifyActionCode());
  }, [load]);

  /**
   * Sent by the client SDK, not Identity (whose password-reset endpoint 202s and sends nothing).
   * `auth/user-not-found` is swallowed HERE so the /reset screen's "if an account exists…" copy
   * is true for every address: a distinguishable failure would be account enumeration.
   */
  const sendPasswordResetEmail = useCallback(
    async (email: string) => {
      const { auth, mod } = await load();
      try {
        await mod.sendPasswordResetEmail(auth, email, { url: `${config.portalOrigin}/login` });
      } catch (err) {
        if ((err as { code?: string })?.code === "auth/user-not-found") return;
        throw err;
      }
    },
    [load],
  );

  /** Re-read emailVerified from the server. The /verify screen never trusts a click. */
  const reloadUser = useCallback(async (): Promise<boolean> => {
    const { auth } = await load();
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    const verified = Boolean(auth.currentUser.emailVerified);
    // reload() refreshes the user record, NOT the ID token. The cached token still says
    // email_verified:false for up to an hour, and /me is what Identity reads it from — so
    // without a forced refresh the next /me bounces the user straight back to /verify.
    if (verified) await auth.currentUser.getIdToken(true);
    // NEVER spread the User. `getIdToken`/`reload` live on its prototype; a copy is a plain
    // object without them, and every getToken() after it throws "getIdToken is not a
    // function" — which mapError then renders as "We couldn't reach the server".
    setUser(auth.currentUser);
    setVerifiedState(verified);
    return verified;
  }, [load]);

  const verifyPasswordResetCode = useCallback(
    async (code: string) => {
      const { auth, mod } = await load();
      return mod.verifyPasswordResetCode(auth, code) as Promise<string>;
    },
    [load],
  );

  const confirmPasswordReset = useCallback(
    async (code: string, password: string) => {
      const { auth, mod } = await load();
      await mod.confirmPasswordReset(auth, code, password);
    },
    [load],
  );

  const signOutFn = useCallback(async () => {
    const { auth, mod } = await load();
    await mod.signOut(auth);
  }, [load]);

  return {
    status,
    getToken,
    emailVerified: verifiedState,
    email: user?.email ?? null,
    signUpWithPassword,
    signInWithPassword,
    signInWithGoogle,
    sendVerificationEmail,
    sendPasswordResetEmail,
    reloadUser,
    verifyPasswordResetCode,
    confirmPasswordReset,
    signOut: signOutFn,
  };
}

export { readKeepSignedIn };
